import {
    type CSSProperties,
    type FC,
    type ReactNode,
    useEffect,
    useState,
} from "react";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import { DASHBOARD_ROW_HEIGHT, type DashboardItemType, type DashboardReport } from "../Dashboard";
import { type DashboardManager, type DashboardReportType } from "../DashboardManager";
import { DashboardBar } from "./DashboardBar";
import { DashboardLine } from "./DashboardLine";
import { DashboardTable } from "./DashboardTable";

export interface DashboardItemProps {
    error: boolean;
    user: string;
    report: DashboardReport | null;
    item: DashboardItemType;
    manager: DashboardManager;
    smallScreen: boolean;
}

export const DashboardItemContent: FC<DashboardItemProps> = ({
    error,
    report,
    user,
    item,
    manager,
    smallScreen,
}) => {
    const [ data, setData ] = useState<null | DashboardReportType>(null);

    const { position, filterUser } = item;

    useEffect(() => {
        if(!report) {
            return;
        }

        manager.loadReport(user, report, filterUser)
            .then(report => {
                setData(report);
            });
    }, [ manager, report, user, filterUser ]);

    const classes: Array<string> = [ "p-2 flex flex-col" ];

    let content: ReactNode;

    if(error) {
        classes.push("do-not-print justify-center items-center bg-neutral-100 dark:bg-dark-800");
        content = (
            <div className="text-lg">
                <FontAwesomeLight icon="exclamation-triangle" />
                Error trying to render item
            </div>
        );
    } else if(!data) {
        classes.push("do-not-print justify-center items-center bg-neutral-100 dark:bg-dark-800");
        content = (
            <div className="text-lg">
                <FontAwesomeLight icon="spinner-third" spin /> Loading...
            </div>
        );
    } else if(data.error || !report) {
        classes.push("do-not-print justify-center items-center bg-neutral-100 dark:bg-dark-800");
        content = (
            <div className="text-lg">
                <FontAwesomeLight icon="times" />
                Failed to load data for graph
            </div>
        );
    } else {
        let reportContent: ReactNode;

        if(report.report.precision && !report.report.on.length) {
            if(report.report.precision.type === "range") {
                reportContent = (
                    <DashboardLine
                        report={data}
                    />
                );
            } else if(report.report.precision.type === "aggregate") {
                reportContent = (
                    <DashboardBar
                        report={data}
                    />
                );
            }
        } else {
            reportContent = (
                <DashboardTable
                    query={data.query}
                    layers={report.report.on.length}
                />
            );
        }

        content = (
            <>
                <div className="font-bold text-lg">{report.name}</div>
                {reportContent}
            </>
        );
    }

    const style: CSSProperties = {};

    if(smallScreen) {
        classes.push("w-full");

        style.height = position.h * DASHBOARD_ROW_HEIGHT;
    } else {
        style.gridRowStart = position.y + 1;
        style.gridRowEnd = "span " + position.h;
        style.gridColumnStart = position.x + 1;
        style.gridColumnEnd = "span " + position.w;
    }

    return (
        <div
            className={classes.join(" ")}
            style={style}
        >
            {content}
        </div>
    );
};
